import type { Role, Position, UserStatus, GeoLevel } from '../types/database'

export interface OfficeRecord {
  id: string
  name: string
  level: string
}

export interface GeoItem {
  id: string
  name: string
}

export interface UserPermissionAssignment {
  userId: string
  permissionId: string
}

export interface UserRecord {
  id: string
  employeeCode?: string
  name?: string
  email?: string
  role?: Role
  position?: Position
  status?: UserStatus
  scopeLevel?: GeoLevel
  scopeStateId?: string
  scopeDistrictId?: string
  scopeTownshipId?: string
  scopeState?: GeoItem
  scopeDistrict?: GeoItem
  scopeTownship?: GeoItem
  isTwoFactorEnabled?: boolean
  createdAt?: string
  updatedAt?: string
  office?: OfficeRecord
  officeId?: string
  allowedStateIds?: string[]
  allowedDistrictIds?: string[]
  stateId?: string
  districtId?: string
  permissions?: UserPermissionAssignment[]
}

export interface GetUsersParams {
  page?: number
  limit?: number
  search?: string
}

export interface GetUsersResponse {
  data: UserRecord[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface UserPayload {
  name: string
  email: string
  password?: string
  employeeCode: string
  role?: Role
  position?: Position
  status?: UserStatus
  scopeLevel?: GeoLevel
  officeId?: string
  scopeStateId?: string
  scopeDistrictId?: string
  scopeTownshipId?: string
  permissionIds?: string[]
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.theone-myanmar.com'

const getHeaders = () => {
  const token = localStorage.getItem('access_token')
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

// -------------------------------------------------------------
// User Management API
// -------------------------------------------------------------

export const getUsers = async (params: GetUsersParams): Promise<GetUsersResponse> => {
  const query = new URLSearchParams()
  if (params.page) query.append('page', params.page.toString())
  if (params.limit) query.append('limit', params.limit.toString())
  if (params.search) query.append('search', params.search)

  const res = await fetch(`${API_BASE_URL}/users?${query.toString()}`, {
    method: 'GET',
    headers: getHeaders(),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to fetch users')
  }

  const rawData = await res.json()

  // Normalize: Handles both plain array [] and paginated response { data: [] }
  if (Array.isArray(rawData)) {
    return {
      data: rawData,
      total: rawData.length,
      page: params.page || 1,
      limit: params.limit || rawData.length,
      totalPages: 1,
    }
  }

  return rawData
}

export const getUserById = async (id: string): Promise<UserRecord> => {
  const res = await fetch(`${API_BASE_URL}/users/${id}`, {
    method: 'GET',
    headers: getHeaders(),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to fetch user details')
  }

  return res.json()
}

export const createUser = async (payload: UserPayload): Promise<UserRecord> => {
  const res = await fetch(`${API_BASE_URL}/users`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to create user')
  }

  return res.json()
}

export const updateUser = async (id: string, payload: UserPayload): Promise<UserRecord> => {
  const res = await fetch(`${API_BASE_URL}/users/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to update user')
  }

  return res.json()
}

export const deleteUser = async (id: string): Promise<void> => {
  const res = await fetch(`${API_BASE_URL}/users/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.message || 'Failed to delete user')
  }

  return res.json().catch(() => ({}))
}

// -------------------------------------------------------------
// Auxiliary Lookup API (Offices, States, Districts, Townships)
// -------------------------------------------------------------

export const getOffices = async (): Promise<OfficeRecord[]> => {
  const res = await fetch(`${API_BASE_URL}/offices`, {
    method: 'GET',
    headers: getHeaders(),
  })

  if (!res.ok) {
    throw new Error('Failed to fetch offices')
  }

  const raw = await res.json()
  return Array.isArray(raw) ? raw : raw.data || []
}

export const getStates = async (): Promise<GeoItem[]> => {
  const res = await fetch(`${API_BASE_URL}/states`, {
    method: 'GET',
    headers: getHeaders(),
  })

  if (!res.ok) {
    throw new Error('Failed to fetch states')
  }

  const raw = await res.json()
  return Array.isArray(raw) ? raw : raw.data || []
}

export const getDistricts = async (stateId: string): Promise<GeoItem[]> => {
  const res = await fetch(`${API_BASE_URL}/districts?stateId=${stateId}`, {
    method: 'GET',
    headers: getHeaders(),
  })

  if (!res.ok) {
    throw new Error('Failed to fetch districts')
  }

  const raw = await res.json()
  return Array.isArray(raw) ? raw : raw.data || []
}

export const getTownships = async (districtId: string): Promise<GeoItem[]> => {
  const res = await fetch(`${API_BASE_URL}/townships?districtId=${districtId}`, {
    method: 'GET',
    headers: getHeaders(),
  })

  if (!res.ok) {
    throw new Error('Failed to fetch townships')
  }

  const raw = await res.json()
  return Array.isArray(raw) ? raw : raw.data || []
}